import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Scissors, Drop, SprayBottle, Leaf, Eye, Sun, Thermometer, Flag,
  ArrowRight, ArrowsClockwise
} from '@phosphor-icons/react';

const ICON_MAP = {
  scissors: Scissors,
  droplet: Drop,
  spray: SprayBottle,
  leaf: Leaf,
  eye: Eye,
  sun: Sun,
  thermometer: Thermometer,
  flag: Flag,
};

const TIMING_COLORS = {
  today: 'bg-red-500/15 text-red-600 dark:text-red-400',
  tomorrow: 'bg-amber-500/15 text-amber-600 dark:text-amber-400',
  this_week: 'bg-blue-500/15 text-blue-600 dark:text-blue-400',
  next_week: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
  this_month: 'bg-muted text-muted-foreground',
};

const TIMING_LABELS = {
  today: 'Today',
  tomorrow: 'Tomorrow',
  this_week: 'This Week',
  next_week: 'Next Week',
  this_month: 'This Month',
};

export const SmartPlanStrip = () => {
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchPlan = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/smart-plan/cached`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setPlan(data);
      }
    } catch { /* ignore */ } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPlan(); }, [fetchPlan]);

  if (loading) return null;
  if (!plan || !plan.actions || plan.actions.length === 0) {
    return (
      <Card className="border border-primary/20 bg-primary/5 cursor-pointer hover:bg-primary/10 transition-colors" onClick={() => navigate('/my-plan')} data-testid="smart-plan-strip-empty">
        <CardContent className="p-4 flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-foreground">Your Smart Lawn Plan</p>
            <p className="text-xs text-muted-foreground mt-0.5">Tap to generate your personalized action plan</p>
          </div>
          <ArrowRight size={18} className="text-primary" />
        </CardContent>
      </Card>
    );
  }

  const top3 = plan.actions.slice(0, 3);

  return (
    <Card className="border border-primary/20 bg-card cursor-pointer hover:shadow-md transition-all" onClick={() => navigate('/my-plan')} data-testid="smart-plan-strip">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-primary">Smart Plan</p>
          <ArrowRight size={14} className="text-muted-foreground" />
        </div>
        <div className="space-y-2">
          {top3.map((a, i) => {
            const Icon = ICON_MAP[a.icon] || Flag;
            const timingColor = TIMING_COLORS[a.timing] || TIMING_COLORS.this_week;
            return (
              <div key={i} className="flex items-center gap-3" data-testid={`plan-strip-item-${i}`}>
                <Icon size={16} className="text-primary shrink-0" weight="bold" />
                <span className="text-sm text-foreground flex-1 truncate">{a.action}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium shrink-0 ${timingColor}`}>
                  {TIMING_LABELS[a.timing] || a.timing}
                </span>
              </div>
            );
          })}
        </div>
        {plan.actions.length > 3 && (
          <p className="text-[10px] text-muted-foreground mt-2 text-right">+{plan.actions.length - 3} more actions</p>
        )}
      </CardContent>
    </Card>
  );
};
